import { useState, useEffect, useMemo } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { ChartBar, X, Lightning, CurrencyDollar, Clock, TrendDown, TrendUp, CaretLeft, CaretRight, CalendarBlank } from '@phosphor-icons/react';
import { api, type EnergyDay } from '../../services/api.js';
import { lockScroll, unlockScroll } from '../../utils/scrollLock.js';

interface Props {
  onClose: () => void;
}

/** Format hours to human-readable: 2ч 42мин */
function fmtDuration(hours: number): string {
  const totalMin = Math.round(hours * 60);
  if (totalMin === 0) return '0 мин';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} мин`;
  if (m === 0) return `${h}ч`;
  return `${h}ч ${m}мин`;
}

interface Tooltip {
  pct: number; // 0-100 horizontal position within chart
  day: EnergyDay;
}

export function EnergyHistory({ onClose }: Props) {
  const [allDays, setAllDays] = useState<EnergyDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);

  useEffect(() => {
    lockScroll();
    return () => unlockScroll();
  }, []);

  useEffect(() => {
    api.getEnergyDaily().then((d) => {
      setAllDays(d);
      if (d.length > 0) {
        setSelectedMonth(d[d.length - 1].date.slice(0, 7));
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const months = useMemo(() => {
    const set = new Set<string>();
    for (const d of allDays) set.add(d.date.slice(0, 7));
    return Array.from(set).sort();
  }, [allDays]);

  const currentMonthIdx = months.indexOf(selectedMonth);
  const prevMonth = () => { if (currentMonthIdx > 0) setSelectedMonth(months[currentMonthIdx - 1]); };
  const nextMonth = () => { if (currentMonthIdx < months.length - 1) setSelectedMonth(months[currentMonthIdx + 1]); };

  const days = useMemo(
    () => allDays.filter((d) => d.date.startsWith(selectedMonth)),
    [allDays, selectedMonth],
  );

  const calendarDays = useMemo(() => {
    if (!selectedMonth) return [];
    const [year, month] = selectedMonth.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const dayMap = new Map(days.map((d) => [d.date, d]));
    return Array.from({ length: daysInMonth }, (_, i) => {
      const dayNum = i + 1;
      const dateStr = `${selectedMonth}-${String(dayNum).padStart(2, '0')}`;
      return { dayNum, dateStr, data: dayMap.get(dateStr) ?? null };
    });
  }, [selectedMonth, days]);

  const maxKwh = Math.max(...days.map((d) => d.kwh), 0.1);
  const totalKwh = days.reduce((s, d) => s + d.kwh, 0);
  const totalCostRub = days.reduce((s, d) => s + d.costRub, 0);
  const totalCostUsd = days.reduce((s, d) => s + d.costUsd, 0);
  const totalRevenue = days.reduce((s, d) => s + d.revenueUsd, 0);
  const totalProfit = totalRevenue - totalCostUsd;
  const avgPower = days.length > 0 ? Math.round(days.reduce((s, d) => s + d.avgPowerW, 0) / days.length) : 0;
  const totalUptime = days.reduce((s, d) => s + d.uptimeHours, 0);
  const totalDowntime = days.reduce((s, d) => s + d.downtimeHours, 0);

  const monthLabel = selectedMonth
    ? new Date(selectedMonth + '-01').toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
    : '';

  // Backend writes `date` as UTC YYYY-MM-DD; match that to flag the in-progress day.
  const todayStr = new Date().toISOString().slice(0, 10);

  const handleBarHover = (e: MouseEvent, data: EnergyDay | null, dayIdx: number, totalDays: number) => {
    if (!data) { setTooltip(null); return; }
    const pct = ((dayIdx + 0.5) / totalDays) * 100;
    setTooltip({ pct, day: data });
  };

  return createPortal(
    <>
      <div class="modal-backdrop" onClick={onClose} />
      <div class="modal energy-modal" role="dialog" aria-modal="true">
        <div class="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ color: 'var(--cyan)', display: 'flex' }}><ChartBar size={16} /></span>
            <span class="modal-title">История энергопотребления</span>
          </div>
          <button class="btn-icon" onClick={onClose} style={{ width: '28px', height: '28px' }}>
            <X size={14} />
          </button>
        </div>

        {/* Month switcher */}
        <div class="energy-month-nav">
          <button class="timeout-btn" disabled={currentMonthIdx <= 0} onClick={prevMonth}>
            <CaretLeft size={14} weight="bold" />
          </button>
          <div class="energy-month-label">
            <CalendarBlank size={14} weight="duotone" />
            <span style={{ textTransform: 'capitalize' }}>{monthLabel}</span>
          </div>
          <button class="timeout-btn" disabled={currentMonthIdx >= months.length - 1} onClick={nextMonth}>
            <CaretRight size={14} weight="bold" />
          </button>
        </div>

        <div class="modal-body" onMouseLeave={() => setTooltip(null)}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--tx-muted)' }}>Загрузка...</div>
          ) : days.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--tx-muted)' }}>
              Нет данных за этот месяц
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div class="energy-summary">
                <SummaryCard icon={Lightning} label="Потребление" value={`${totalKwh.toFixed(1)} кВтч`} sub={`~${avgPower} Вт`} />
                <SummaryCard icon={CurrencyDollar} label="Стоимость" value={`${totalCostRub.toFixed(0)} ₽`} sub={`$${totalCostUsd.toFixed(2)}`} />
                <SummaryCard
                  icon={totalProfit >= 0 ? TrendUp : TrendDown}
                  label={totalProfit >= 0 ? 'Прибыль' : 'Убыток'}
                  value={`$${Math.abs(totalProfit).toFixed(2)}`}
                  color={totalProfit >= 0 ? 'var(--lime)' : 'var(--red)'}
                />
                <SummaryCard icon={Clock} label="Аптайм" value={fmtDuration(totalUptime)} sub={`простой: ${fmtDuration(totalDowntime)}`} />
              </div>

              {/* Daily bar chart */}
              <div class="energy-chart-label">кВтч по дням</div>
              <div class="energy-chart">
                {calendarDays.map(({ dayNum, data }) => {
                  const kwh = data?.kwh ?? 0;
                  const pct = (kwh / maxKwh) * 100;
                  const hasData = data !== null;
                  const profit = data ? data.profitUsd >= 0 : true;

                  return (
                    <div
                      key={dayNum}
                      class={`energy-bar-col${!hasData ? ' no-data' : ''}`}
                      onMouseEnter={(e) => handleBarHover(e as unknown as MouseEvent, data, dayNum - 1, calendarDays.length)}
                      onMouseLeave={() => setTooltip(null)}
                      onClick={() => {
                        if (!data) return;
                        setTooltip((prev) =>
                          prev?.day.date === data.date ? null : { pct: ((dayNum - 0.5) / calendarDays.length) * 100, day: data }
                        );
                      }}
                    >
                      <span class="energy-bar-value">{hasData ? `${kwh.toFixed(2)} кВтч` : ''}</span>
                      <div class="energy-bar-track">
                        <div
                          class="energy-bar-fill"
                          style={{
                            height: `${Math.max(hasData ? pct : 0, hasData ? 4 : 0)}%`,
                            background: hasData ? (profit ? 'var(--lime)' : 'var(--red)') : 'var(--bd-dim)',
                          }}
                        />
                      </div>
                      <span class="energy-bar-day">{dayNum}</span>
                    </div>
                  );
                })}

                {/* Tooltip */}
                {tooltip && (
                <div
                  class="energy-tooltip"
                  style={{
                    left: `clamp(0px, calc(${tooltip.pct}% - 140px), calc(100% - 280px))`,
                  }}
                >
                  <div class="energy-tooltip-date">
                    {new Date(tooltip.day.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                    {tooltip.day.date === todayStr && <span class="energy-today-badge">неполный</span>}
                  </div>
                  {tooltip.day.date === todayStr && (
                    <div class="energy-tooltip-note">
                      День не завершён: доход — за 24ч, расход — с начала суток, прибыль уточнится к концу дня.
                    </div>
                  )}
                  <div class="energy-tooltip-row">
                    <span>Потребление</span>
                    <strong>{tooltip.day.kwh.toFixed(1)} кВтч</strong>
                  </div>
                  <div class="energy-tooltip-row">
                    <span>Мощность</span>
                    <strong>{tooltip.day.avgPowerW} Вт</strong>
                  </div>
                  <div class="energy-tooltip-row">
                    <span>Стоимость</span>
                    <strong>{tooltip.day.costRub.toFixed(1)} ₽ (${tooltip.day.costUsd.toFixed(2)})</strong>
                  </div>
                  <div class="energy-tooltip-row">
                    <span>Доход майнинга</span>
                    <strong>${tooltip.day.revenueUsd.toFixed(2)}</strong>
                  </div>
                  <div class="energy-tooltip-row">
                    <span>{tooltip.day.profitUsd >= 0 ? 'Прибыль' : 'Убыток'}</span>
                    <strong style={{ color: tooltip.day.profitUsd >= 0 ? 'var(--lime)' : 'var(--red)' }}>
                      {tooltip.day.profitUsd >= 0 ? '+' : '-'}${Math.abs(tooltip.day.profitUsd).toFixed(2)}
                    </strong>
                  </div>
                  {tooltip.day.blocksToday != null && tooltip.day.blocksToday > 0 && (
                    <div class="energy-tooltip-row">
                      <span>Блоки</span>
                      <strong style={{ color: 'var(--purple)' }}>{tooltip.day.blocksToday}</strong>
                    </div>
                  )}
                  <div class="energy-tooltip-row">
                    <span>Фермы</span>
                    <strong>{tooltip.day.onlineRigs} онлайн / {tooltip.day.offlineRigs} офлайн</strong>
                  </div>
                  <div class="energy-tooltip-row">
                    <span>Аптайм</span>
                    <strong>{fmtDuration(tooltip.day.uptimeHours)} / простой {fmtDuration(tooltip.day.downtimeHours)}</strong>
                  </div>
                  <div class="energy-tooltip-row">
                    <span>Тариф</span>
                    <strong>{tooltip.day.electricityRate} ₽/кВтч</strong>
                  </div>
                </div>
              )}
              </div>

              {/* Uptime timeline */}
              <div class="energy-chart-label" style={{ marginTop: 'var(--space-4)' }}>Жизнь ферм</div>
              <div class="energy-uptime-bar">
                {calendarDays.map(({ dayNum, data }) => {
                  const hasData = data !== null;
                  const totalH = hasData ? data!.uptimeHours + data!.downtimeHours : 0;
                  const uptimePct = totalH > 0 ? (data!.uptimeHours / totalH) * 100 : 0;
                  const downtimePct = 100 - uptimePct;

                  return (
                    <div
                      key={dayNum}
                      class="energy-uptime-cell"
                      style={hasData
                        ? { background: `linear-gradient(to top, var(--red) ${downtimePct}%, var(--lime) ${downtimePct}%)` }
                        : { background: 'var(--bd-dim)', opacity: 0.15 }
                      }
                      title={hasData
                        ? `${data!.date}: ${fmtDuration(data!.uptimeHours)} онлайн (${Math.round(uptimePct)}%), ${fmtDuration(data!.downtimeHours)} офлайн (${Math.round(downtimePct)}%)`
                        : `День ${dayNum}: нет данных`}
                    />
                  );
                })}
              </div>
              <div class="energy-uptime-legend">
                <span><span class="energy-legend-dot" style={{ background: 'var(--lime)' }} /> онлайн</span>
                <span><span class="energy-legend-dot" style={{ background: 'var(--red)' }} /> офлайн</span>
                <span><span class="energy-legend-dot" style={{ background: 'var(--bd-dim)', opacity: 0.15 }} /> нет данных</span>
              </div>

              {/* Daily details table */}
              <div class="energy-chart-label" style={{ marginTop: 'var(--space-4)' }}>Расходы и прибыль по дням</div>
              <div class="table-container" style={{ maxHeight: '200px' }}>
                <table class="data-table energy-table">
                  <thead>
                    <tr><th>Дата</th><th>кВтч</th><th>Расход ₽</th><th>Доход ₽</th><th>Прибыль ₽</th><th>Блоки</th><th>Аптайм</th></tr>
                  </thead>
                  <tbody>
                    {[...days].reverse().map((d) => {
                      const usdToRub = d.costUsd > 0 ? d.costRub / d.costUsd : 90;
                      const revenueRub = d.revenueUsd * usdToRub;
                      const profitRub = revenueRub - d.costRub;
                      const isProfit = profitRub >= 0;
                      const dayLabel = new Date(d.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
                      const isToday = d.date === todayStr;
                      return (
                        <tr key={d.date} class={isToday ? 'energy-today-row' : undefined}>
                          <td>
                            {dayLabel}
                            {isToday && <span class="energy-today-badge" title="День не завершён: доход показан за последние 24ч, расход — с начала суток">сегодня · неполный</span>}
                          </td>
                          <td style={{ fontFamily: 'var(--font-mono)' }}>{d.kwh.toFixed(1)}</td>
                          <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--red)' }}>{d.costRub.toFixed(0)} ₽</td>
                          <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--lime)' }}>{revenueRub.toFixed(0)} ₽</td>
                          <td style={{ fontFamily: 'var(--font-mono)', color: isProfit ? 'var(--lime)' : 'var(--red)' }}>
                            {isProfit ? '+' : '-'}{Math.abs(profitRub).toFixed(0)} ₽
                          </td>
                          <td style={{ fontFamily: 'var(--font-mono)', color: (d.blocksToday ?? 0) > 0 ? 'var(--purple)' : 'var(--tx-muted)' }}>
                            {(d.blocksToday ?? 0) > 0 ? d.blocksToday : '—'}
                          </td>
                          <td style={{ fontFamily: 'var(--font-mono)' }}>{fmtDuration(d.uptimeHours)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div class="modal-footer">
          <button class="btn btn-primary" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function SummaryCard({ icon: Icon, label, value, sub, color }: {
  icon: typeof Lightning;
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div class="energy-summary-card">
      <span style={{ color: color ?? 'var(--tx-muted)', display: 'flex' }}><Icon size={14} weight="duotone" /></span>
      <div>
        <div style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)', color: color ?? 'var(--tx-white)' }}>{value}</div>
        <div style={{ fontSize: '0.625rem', color: 'var(--tx-muted)' }}>{label}{sub ? ` · ${sub}` : ''}</div>
      </div>
    </div>
  );
}
